<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import InboxesAPI from 'dashboard/api/inboxes';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'next/button/Button.vue';

const POLL_INTERVAL_MS = 3000;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const inboxName = ref('');
const connection = ref(null);
const isCreating = ref(false);
const isRefreshing = ref(false);
const isDisconnecting = ref(false);
const isPolling = ref(false);
const pollFailures = ref(0);
const hasCompleted = ref(false);
const routeOperationId = String(route.query.operation || '');
const operationId = ref(
  UUID_PATTERN.test(routeOperationId) ? routeOperationId : crypto.randomUUID()
);
let pollTimer;
let isUnmounted = false;
let pollGeneration = 0;

const qrPayload = computed(() => connection.value?.qr || {});
const rawQrImage = computed(
  () =>
    qrPayload.value.base64 ||
    qrPayload.value.qrcode?.base64 ||
    qrPayload.value.code?.base64 ||
    ''
);
const qrImage = computed(() => {
  if (!rawQrImage.value) return '';
  return rawQrImage.value.startsWith('data:image')
    ? rawQrImage.value
    : `data:image/png;base64,${rawQrImage.value}`;
});
const isConnected = computed(() => connection.value?.status === 'open');
const isManagingConnection = Boolean(route.query.connection);
const connectionStatus = computed(() => {
  const status = connection.value?.status;
  if (status === 'open') return 'connected';
  if (['creating', 'connecting'].includes(status)) return 'reconnecting';
  if (status === 'error') return 'error';
  return 'disconnected';
});
const reconnectLabel = computed(() =>
  connectionStatus.value === 'reconnecting'
    ? t('INBOX_MGMT.ADD.WHATSAPP.QR.REFRESH_BUTTON')
    : t('INBOX_MGMT.ADD.WHATSAPP.QR.RECONNECT_BUTTON')
);
const canCreate = computed(() => inboxName.value.trim().length > 0);
const isConnectionActionPending = computed(
  () => isRefreshing.value || isDisconnecting.value
);

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = undefined;
  }
};

const completeSetup = () => {
  if (isManagingConnection || hasCompleted.value || isUnmounted) return;
  hasCompleted.value = true;
  stopPolling();
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: {
      page: 'new',
      inbox_id: connection.value.inbox_id,
    },
  });
};

const pollStatus = async () => {
  if (!connection.value?.id || isPolling.value) return;
  const generation = pollGeneration;
  isPolling.value = true;
  try {
    const { data } = await InboxesAPI.getWhatsappQrConnection(
      connection.value.id
    );
    if (isUnmounted || generation !== pollGeneration) return;
    connection.value = { ...connection.value, ...data };
    pollFailures.value = 0;
    if (data.status === 'open') completeSetup();
  } catch (error) {
    if (isUnmounted || generation !== pollGeneration) return;
    pollFailures.value += 1;
    if (pollFailures.value >= 3) {
      stopPolling();
      useAlert(
        parseAPIErrorResponse(error) ||
          t('INBOX_MGMT.ADD.WHATSAPP.QR.STATUS_ERROR')
      );
    }
  } finally {
    isPolling.value = false;
  }
};

const startPolling = () => {
  if (isUnmounted) return;
  stopPolling();
  pollTimer = setInterval(pollStatus, POLL_INTERVAL_MS);
};

const createConnection = async () => {
  if (!canCreate.value || isCreating.value) return;
  isCreating.value = true;
  try {
    await router.replace({
      query: {
        ...route.query,
        operation: operationId.value,
      },
    });
    const { data } = await InboxesAPI.createWhatsappQrConnection(
      inboxName.value.trim(),
      operationId.value
    );
    if (isUnmounted) return;
    connection.value = data;
    await router.replace({
      query: {
        ...route.query,
        connection: data.id,
        operation: operationId.value,
      },
    });
    if (data.status === 'open') {
      completeSetup();
    } else {
      startPolling();
    }
  } catch (error) {
    if (isUnmounted) return;
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INBOX_MGMT.ADD.WHATSAPP.QR.CREATE_ERROR')
    );
  } finally {
    isCreating.value = false;
  }
};

const reconnect = async () => {
  if (isConnectionActionPending.value) return;
  isRefreshing.value = true;
  try {
    const { data } = await InboxesAPI.getWhatsappQrCode(connection.value.id);
    if (isUnmounted) return;
    connection.value = {
      ...connection.value,
      status: data.status,
      qr: data.qr,
    };
    if (!isConnected.value) startPolling();
  } catch (error) {
    if (isUnmounted) return;
    useAlert(
      parseAPIErrorResponse(error) || t('INBOX_MGMT.ADD.WHATSAPP.QR.QR_ERROR')
    );
  } finally {
    isRefreshing.value = false;
  }
};

const disconnect = async () => {
  if (isConnectionActionPending.value) return;
  pollGeneration += 1;
  stopPolling();
  isDisconnecting.value = true;
  try {
    await InboxesAPI.disconnectWhatsappQrConnection(connection.value.id);
    if (isUnmounted) return;
    stopPolling();
    connection.value = {
      ...connection.value,
      status: 'disconnected',
      qr: null,
    };
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.QR.DISCONNECTED'));
  } catch (error) {
    if (isUnmounted) return;
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INBOX_MGMT.ADD.WHATSAPP.QR.DISCONNECT_ERROR')
    );
  } finally {
    isDisconnecting.value = false;
  }
};

onBeforeUnmount(() => {
  isUnmounted = true;
  pollGeneration += 1;
  stopPolling();
});

onMounted(async () => {
  const connectionId = String(route.query.connection || '');
  if (!connectionId) return;
  connection.value = { id: connectionId };
  await pollStatus();
  if (isUnmounted) return;
  if (!isConnected.value && connection.value?.status !== 'disconnected') {
    await reconnect();
    if (isUnmounted) return;
    startPolling();
  }
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2 text-start">
      <div
        class="flex size-11 items-center justify-center rounded-full bg-n-alpha-2"
      >
        <Icon icon="i-woot-whatsapp" class="text-n-slate-10 size-6" />
      </div>
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.QR.TITLE') }}
      </h3>
      <p class="text-sm leading-6 text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.QR.DESCRIPTION') }}
      </p>
    </div>

    <div class="rounded-xl border border-n-amber-6 bg-n-amber-2 p-4">
      <p class="text-sm font-medium text-n-amber-12">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.QR.WARNING_TITLE') }}
      </p>
      <p class="mt-1 text-sm leading-6 text-n-amber-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.QR.WARNING_BODY') }}
      </p>
    </div>

    <form
      v-if="!connection"
      class="flex flex-col gap-4"
      @submit.prevent="createConnection"
    >
      <label class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.QR.INBOX_NAME') }}
        </span>
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.QR.INBOX_PLACEHOLDER')"
        />
      </label>
      <NextButton
        type="submit"
        solid
        blue
        :disabled="!canCreate || isCreating"
        :is-loading="isCreating"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.QR.CREATE_BUTTON')"
      />
    </form>

    <div v-else class="flex flex-col items-center gap-4">
      <p class="text-sm text-n-slate-11">
        {{ $t(`INBOX_MGMT.ADD.WHATSAPP.QR.STATUS.${connectionStatus}`) }}
      </p>
      <p v-if="!isConnected" class="text-center text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.QR.SCAN_INSTRUCTIONS') }}
      </p>

      <img
        v-if="qrImage && !isConnected"
        :src="qrImage"
        :alt="$t('INBOX_MGMT.ADD.WHATSAPP.QR.QR_ALT')"
        class="size-72 rounded-xl border border-n-weak bg-white p-3"
      />

      <div class="flex gap-3">
        <NextButton
          v-if="!isConnected"
          faded
          slate
          :disabled="isConnectionActionPending"
          :is-loading="isRefreshing"
          :label="reconnectLabel"
          @click="reconnect"
        />
        <NextButton
          v-if="connection.status !== 'disconnected'"
          faded
          ruby
          :disabled="isConnectionActionPending"
          :is-loading="isDisconnecting"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.QR.DISCONNECT_BUTTON')"
          @click="disconnect"
        />
      </div>
    </div>
  </div>
</template>
